import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  Button,
  Spin,
  Form,
  Alert,
  Empty,
  Checkbox,
} from "antd";
import { CheckCircleTwoTone, InfoCircleTwoTone } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  useCreateProcessAppointmentPaymentMutation,
  useGetCalendarAvailabilityQuery,
} from "../../redux/features/routeQuestionApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { toast } from "react-toastify";
import { leadTimeBlockedDateStringsCST } from "../../utils/bookingFlowUtil";

dayjs.extend(utc);
dayjs.extend(timezone);


export default function StepSchedule({
  formData,
  setFormData,
  onNext,
  onBack,
  resetToFirstStep,
}) {
  const selectedProviderId = formData?.selectedProvider?.member_id || null;
  const [selectedDate, setSelectedDate] = useState(null); 
  const [selectedSlot, setSelectedSlot] = useState(null); 
  const [showPayment, setShowPayment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [card, setCard] = useState(null);
  const squareContainerRef = useRef(null);

  const [createProcessAppointmentPayment, { isLoading: ProcessAppointmentLoading }] =
    useCreateProcessAppointmentPaymentMutation();

  const today = useMemo(
    () => dayjs().tz("America/Chicago").startOf("day"),
    []
  );
  // Keep this range identical to the availability preview. The API returns the
  // definitive list of bookable dates and slots for this window.
  const lastAvailabilityDay = useMemo(
    () => dayjs().tz("America/Chicago").add(30, "day").endOf("day"),
    []
  );

  const fromDateStr = useMemo(() => today.format("YYYY-MM-DD"), [today]);
  const toDateStr = useMemo(
    () => lastAvailabilityDay.format("YYYY-MM-DD"),
    [lastAvailabilityDay]
  );
  // fromDateStr/toDateStr describe what we ASK for, not what we get back: the
  // API advances the window when from_date is not bookable, so it can return a
  // day past to_date. Re-clamping to our own request dropped that last day in
  // step 7 while Check Availability still listed it.

  const {
    data: availabilityResponse,
    isLoading: loadingAvailability,
  } = useGetCalendarAvailabilityQuery(
    selectedProviderId
      ? {
          provider_id: selectedProviderId,
          from_date: fromDateStr,
          to_date: toDateStr,
          visit_type: formData?.service?.visit_type_id,
        }
      : skipToken,
    { skip: !selectedProviderId }
  );

  const availability = availabilityResponse?.availability;
  const availableSlots = availability?.available_slots || [];
  const userFacilityTimezone = availability?.user_facility_timezone;

  const slotMap = useMemo(() => {
    return availableSlots.reduce((acc, obj) => {
      const [date, slots] = Object.entries(obj)[0];
      // Be defensive if the API sends more than one entry for a date.
      acc[date] = [...(acc[date] || []), ...(Array.isArray(slots) ? slots : [])];
      return acc;
    }, {});
  }, [availableSlots]);

  // Bounds taken from the response itself, falling back to the requested window
  // only while the request is still in flight.
  const [rangeStartStr, rangeEndStr] = useMemo(() => {
    const dates = Object.keys(slotMap).sort();
    return dates.length
      ? [dates[0], dates[dates.length - 1]]
      : [fromDateStr, toDateStr];
  }, [slotMap, fromDateStr, toDateStr]);

  const calendarRange = useMemo(
    // Plain local dayjs values: antd compares these against calendar cells,
    // which are local too. A zoned value here shifts the range by a day.
    //
    // endOf("day") on the upper bound is required, not cosmetic. antd checks
    // `isAfter(cell, validRange[1])` as a raw timestamp with no date
    // granularity, and its cells carry the current time of day, so a bound at
    // midnight disables the whole final day of the range.
    () => [dayjs(rangeStartStr).startOf("day"), dayjs(rangeEndStr).endOf("day")],
    [rangeStartStr, rangeEndStr]
  );

  const disabledDate = (current) => {
    if (!current) return true;

    const dateStr = current.format("YYYY-MM-DD");
    if (dateStr < rangeStartStr || dateStr > rangeEndStr) {
      return true;
    }

    // Lead-time policy. Evaluated per call rather than memoised so a session
    // left open across 3:00 PM CT starts blocking without a reload.
    if (leadTimeBlockedDateStringsCST().includes(dateStr)) return true;

    // `available_slots` is the API's live source of truth. `working_days` is
    // only a schedule summary and can be false even when a dated exception or
    // an open slot is returned, which previously hid slots shown in Check
    // Availability.
    if (!slotMap[dateStr] || slotMap[dateStr].length === 0) return true;

    return false;
  };

  const destroySquareCard = async () => {
    try {
      if (card && typeof card.destroy === "function") {
        await card.destroy();
      }
    } catch (e) {
    } finally {
      setCard(null);
      if (squareContainerRef.current) {
        squareContainerRef.current.innerHTML = "";
      }
    }
  };

  useEffect(() => {
    let isMounted = true;

    async function initSquare() {
      if (!showPayment || !selectedDate || !selectedSlot) return;

      if (!window.Square) {
        toast.error("Square SDK not loaded");
        return;
      }

      if (document.querySelector("#card-container iframe")) return;

      try {
        const paymentsInstance = window.Square.payments(
          process.env.REACT_APP_SQUARE_APP_ID || "sq0idp-fyCs-W2rP6zdy7eesrEoIw",
          process.env.REACT_APP_SQUARE_LOCATION_ID || "L5ZJHEG3TG2CQ"
        );

        if (!isMounted) return;

        const cardInstance = await paymentsInstance.card();
        await cardInstance.attach("#card-container");

        if (!isMounted) {
          try {
            await cardInstance.destroy?.();
          } catch (_) {}
          return;
        }

        setCard(cardInstance);
      } catch (err) {
        console.error("Square init failed:", err);
        toast.error("Failed to load payment form");
      }
    }

    initSquare();

    return () => {
      isMounted = false;
    };
  }, [showPayment, selectedDate, selectedSlot]);

  useEffect(() => {
    return () => {
      destroySquareCard();
    };
  }, []);

  useEffect(() => {
    if (!showPayment) {
      destroySquareCard();
    }
  }, [showPayment]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setShowPayment(false);
    destroySquareCard();
  };

  const calculateDuration = (start, end) => {
  const startTime = dayjs(start, "hh:mm A");
  const endTime = dayjs(end, "hh:mm A");
  return endTime.diff(startTime, "minute");
};
  const handleSlotSelect = (slot) => {
    const duration = calculateDuration(slot.start_time, slot.end_time);
    setSelectedSlot({ ...slot, duration_in_minutes: duration });
    setShowPayment(true);
  };


  const getAppointmentPayload = () => {
    // Get patient_id from multiple possible keys (same logic as StepQuestionnaire)
    const patient_id = formData?.patientid || formData?.patient_id || formData?.patientId || formData?.data?.patientid || formData?.data?.patient_id;
    
    return {
      patient_id: patient_id,
      facility_id: formData?.submission_info?.facility_id,
      member_id: selectedProviderId,
      mode: "Phone call",
      repetition: "Single Date",
      start_date: selectedDate.format("YYYY-MM-DD"),
      start_time: selectedSlot.start_time,
      questionnaire: formData?.data?.questionnaire_details?.ques_map_id,
      visit_type_id: formData?.service?.visit_type_id,
      duration_in_minutes: selectedSlot.duration_in_minutes || 10,
    };
  };

  const handlePay = async () => {
    if (!selectedProviderId) {
      toast.error("Provider is missing. Go back and select a provider.");
      return;
    }
    if (!selectedDate || !selectedSlot) {
      toast.error("Please select a date and time.");
      return;
    }
    if (!policyAccepted) {
      toast.error("Please read and accept the cancellation policy.");
      return;
    }
    if (!card) {
      toast.error("Payment form not ready");
      return;
    }

    // Validate patient_id exists
    const patient_id = formData?.patientid || formData?.patient_id || formData?.patientId || formData?.data?.patientid || formData?.data?.patient_id;
    if (!patient_id) {
      toast.error("Patient ID is missing. Please go back and complete patient information first.");
      return;
    }

    setLoading(true);

    try {
      const result = await card.tokenize();

      if (result.status !== "OK" || !result.token) {
        toast.error("Payment tokenization failed. Try again.");
        setLoading(false);
        return;
      }

      // Get appointment payload
      const appointmentPayload = getAppointmentPayload();

      // Combine appointment payload with payment data
      const paymentPayload = {
        appointment: appointmentPayload,
        payment_source_id: result.token,
        payment_amount: 1,
        patientid: patient_id,
      };

      const res = await createProcessAppointmentPayment(paymentPayload).unwrap();

      toast.success(
        `Booking confirmed for ${formData?.service?.title || "your service"} on ${(
          formData?.schedule?.date || selectedDate?.format("YYYY-MM-DD")
        )} at ${(formData?.schedule?.start_time || selectedSlot?.start_time) || ""}`
      );

      setFormData?.({
        ...formData,
        schedule: {
          date: selectedDate.format("YYYY-MM-DD"),
          ...selectedSlot,
        },
        payment: {
          data: res,
        },
      });

      localStorage.removeItem("currentStep");
      localStorage.removeItem("formData");

      if (typeof resetToFirstStep === "function") {
        resetToFirstStep();
      } else if (typeof onNext === "function") {
        onNext();
      }

      setShowPayment(false);
      setSelectedSlot(null);
      destroySquareCard();
    } catch (err) {
      console.error("Payment error:", err);
      toast.error(
        err?.data?.message || err?.message || "Payment error. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = async () => {
    await destroySquareCard();
    setShowPayment(false);
    if (typeof onBack === "function") onBack();
  };


  const renderProviderSummary = () => (
    <div style={{ marginBottom: 20, width: "100%" }}>
      <h2 className="form-title text-white" style={{ padding: "10px 0" }}>
        {formData?.service?.visit_type}
      </h2>
      <div
        style={{
          marginBottom: 16,
          padding: "16px",
          backgroundColor: "#fff",
          borderRadius: 8,
          border: "1px solid #d9d9d9",
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 16, color: "#000" }}>
          Provider
        </div>
        <div style={{ color: "#333", marginTop: 4 }}>
          {formData?.selectedProvider?.display_name || "—"}
        </div>
        <p style={{ marginTop: 12, marginBottom: 0, fontSize: 13, color: "#666" }}>
          To change provider or service, use <b>Back</b> to return to earlier steps.
        </p>
      </div>
    </div>
  );

  const renderCalendar = () => {
    if (!selectedProviderId) return null;

    return (
      <>
        <h2 className="schedule-title">Select a Date & Time</h2>

        <div className="calendar-container">
          <Calendar
            fullscreen={false}
            disabledDate={disabledDate}
            onSelect={handleDateSelect}
            validRange={calendarRange}
          />
        </div>

        <div style={{ marginTop: 8 }}>
          <Alert
            type="info"
            showIcon
            icon={<InfoCircleTwoTone twoToneColor="#fc8c02 " />}
            message={
              <span>
                <b>All times are shown in Central Time (CT).</b> Calendar
                range: <b>{dayjs(rangeStartStr).format("MMM D, YYYY")}</b> to{" "}
                <b>{dayjs(rangeEndStr).format("MMM D, YYYY")}</b>
                {userFacilityTimezone ? (
                  <>
                    {" "}
                    (Facility API timezone: <b>{userFacilityTimezone}</b>)
                  </>
                ) : null}
                . Only dates with live availability can be selected. Same-day
                and next-day slots are not available after 3:00 PM CT.
              </span>
            }
          />
        </div>
      </>
    );
  };

  const renderSlotGrid = () => {
    if (!selectedDate) return null;

    const dateKey = selectedDate.format("YYYY-MM-DD");
    const slots = slotMap[dateKey] || [];

    if (!slots.length) {
      return (
        <div style={{ marginTop: 16 }}>
          <Empty description="No slots available for this date" />
        </div>
      );
    }

    return (
      <div className="slot-grid" style={{ marginTop: 16, display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
}}>
        {slots.map((slot, idx) => {
          const slotLabel = `${slot.start_time} - ${slot.end_time}`;
          const isSelected =
            selectedSlot &&
            selectedSlot.start_time === slot.start_time &&
            selectedSlot.end_time === slot.end_time;

        return (
            <div
              key={`${slot.start_time}-${slot.end_time}-${idx}`}
              className={`slot-card ${isSelected ? "selected" : ""}`}
              onClick={() => handleSlotSelect(slot)}
              style={{
                position: "relative",
                border: isSelected ? "2px solid #fc8c02" : "1px solid #e5e5e5",
                borderRadius: 8,
                padding: "12px 12px",
                cursor: "pointer",
                background: isSelected ? "rgba(223, 225, 228, 0.06)" : "#fff",
                transition: "all .15s ease",
                color: isSelected ? "#fff" : "#000"
              }}
            >
              {isSelected && (
                <CheckCircleTwoTone
                  twoToneColor="#fc8c02"
                  className="check-icon"
                  style={{ position: "absolute", right: 2, top: 2, fontSize: 18 }}
                />
              )}
              <div style={{ fontWeight: 600 }}>{slotLabel}</div>
              {slot?.duration_in_minutes ? (
                <div style={{ fontSize: 12, color: "#666" }}>
                  Duration: {slot.duration_in_minutes} min
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  const renderPaymentSection = () => {
    if (!showPayment) return null;

    return (
      <div style={{ width: "100%", marginTop: 24 }}>
        <h2 className="schedule-title" style={{ color: "#fff" }}>
          Confirm Your Payment
        </h2>

        <div
          id="card-container"
          ref={squareContainerRef}
          className="my-6 border p-4 rounded bg-white"
          style={{ marginTop: 12 }}
        />

        <div style={{ marginTop: 16 }}>
          <Checkbox
            checked={policyAccepted}
            onChange={(e) => setPolicyAccepted(e.target.checked)}
          >
            <span className="text-white">
              I have read and accept the{" "}
              <Link
                to="/cancellation-policy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#fc8c02" }}
              >
                no-show and cancellation policy
              </Link>
              .
            </span>
          </Checkbox>
        </div>
      </div>
    );
  };

  const renderFooterButtons = () => {
    return (
      <Form.Item style={{ width: "100%", marginTop: "30px" }}>
        <div style={{ display: "flex", gap: "1rem" }}>
          <Button className="btn-css-class" onClick={handleBack} block>
            Back
          </Button>

          <Button
            type="primary"
            size="large"
            block
            disabled={
              !selectedDate || !selectedSlot || !showPayment || !policyAccepted
            }
            onClick={handlePay}
            loading={loading || ProcessAppointmentLoading}
            className="btn-css"
          >
            Pay & Confirm
          </Button>
        </div>
      </Form.Item>
    );
  };

  if (!selectedProviderId) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Provider not selected"
        description="Please go back and choose a provider before scheduling."
      />
    );
  }

  return (
    <div className="step-schedule" style={{ width: "100%" }}>
      <div style={{ marginBottom: 12 }}>
        <h3 className="schedule-title text-white">
          Schedule Your Appointment
        </h3>
        <p className="text-white">
          Pick a date and time for the next 30 days. Times are interpreted in{" "}
          <strong>Central Time (CT)</strong>.
        </p>
      </div>

      {renderProviderSummary()}

      {selectedProviderId && loadingAvailability && (
        <div
          style={{
            height: 140,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 12,
          }}
        >
          <Spin />
        </div>
      )}

   

      {renderCalendar()}

      {selectedDate && (
        <div style={{ marginTop: 8 }}>
          <p className="text-white">
            Selected date: <b>{selectedDate.format("dddd, MMM D, YYYY")}</b>
          </p>
        </div>
      )}

      {renderSlotGrid()}

      {renderPaymentSection()}

      {renderFooterButtons()}
    </div>
  );
}

